import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import styled from 'styled-components';
import { darkCustomPalette } from '@/assets/branding/defaultTheme';
import type { DefaultTheme } from '@mui/styled-engine-sc';
import { forwardRef, type SVGProps, type ForwardRefExoticComponent, type RefAttributes, type FC } from 'react';
import { SvgIcon } from '@mui/material';

interface StyledProps {
  $isActive: boolean;
  fontSize?: 'small' | 'medium' | 'large';
}

interface SvgIconButtonProps extends StyledProps, IconButtonProps {
  component: FC<SVGProps<SVGSVGElement>>;
}

export const SvgIconButton: ForwardRefExoticComponent<Omit<SvgIconButtonProps, 'ref'> & RefAttributes<HTMLButtonElement>> = forwardRef(
  ({ $isActive, component, onClick, fontSize, ...props }, ref) => (
    <StyledIconButton onClick={onClick} $isActive={$isActive} size={fontSize ?? 'medium'} ref={ref} {...props}>
      <SvgIcon component={component} inheritViewBox fontSize={fontSize ?? 'medium'} />
    </StyledIconButton>
  )
);

SvgIconButton.displayName = 'SvgIconButton';

const StyledIconButton = styled(IconButton)<StyledProps>`
  color: ${({ $isActive, theme }): string => getColor(theme, $isActive)};
  background: ${({ $isActive, theme }): string => ($isActive ? getButtonBackground(theme) : 'none')};
  &:hover {
    background: ${({ theme }): string => getButtonBackground(theme)};
    filter: none;
  }
  & svg {
    filter: ${({ theme }): string => (theme.mode === 'dark' ? dropShadow : 'none')};
  }
`;

export const getColor = (theme: DefaultTheme, $isActive: boolean, $enhanceVisibility?: boolean): string =>
  $isActive
    ? theme.palette.primary.main
    : $enhanceVisibility
      ? darkCustomPalette.panels.threeDButton.font
      : theme.customPalette.panels.threeDButton.font;

export const getButtonBackground = (theme: DefaultTheme, $enhanceVisibility?: boolean): string =>
  $enhanceVisibility
    ? darkCustomPalette.panels.threeDButton.background
    : theme.customPalette.panels.threeDButton.background;

export const dropShadow = `drop-shadow(0 0 1px rgba(0, 0, 0, 1)) drop-shadow(0 0 2px rgba(0, 0, 0, 1))
drop-shadow(0 0 4px rgba(0, 0, 0, 0.6));`;